import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { TextField, AreaField, SelectField } from "@/components/forms/Field";
import { AddressField } from "@/components/forms/AddressField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDropzone } from "@/components/forms/FileDropzone";
import { FINANCING_TYPES, US_STATES, CREDIT_RANGES } from "@/data/site";
import api, { formatApiError } from "@/lib/api";
import { API } from "@/lib/api";
import { LenderMatch } from "@/components/site/LenderMatch";
import { toast } from "sonner";

const STORAGE_KEY = "bec_apply_draft";
const STEPS = ["Financing Type", "Contact", "Deal", "Borrower", "Documents", "Review"];

export default function Apply() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
  });
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (n, v) => setForm((f) => ({ ...f, [n]: v }));

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); }, [form]);

  // preselect type from query and route EMD/Transactional to dedicated forms
  useEffect(() => {
    const t = params.get("type");
    if (t) {
      const match = FINANCING_TYPES.find((x) => x.value === t);
      if (match) {
        if (match.kind === "emd") { navigate("/emd-funding"); return; }
        if (match.kind === "transactional") { navigate("/transactional-funding"); return; }
        set("financing_type", t);
        setStep(1);
      }
    }
    // eslint-disable-next-line
  }, []);

  const chooseType = (val) => {
    const match = FINANCING_TYPES.find((x) => x.value === val);
    if (!match) return;
    if (match.kind === "emd") { navigate("/emd-funding"); return; }
    if (match.kind === "transactional") { navigate("/transactional-funding"); return; }
    set("financing_type", val);
  };

  const typeLabel = useMemo(() => FINANCING_TYPES.find((x) => x.value === form.financing_type)?.label, [form.financing_type]);
  const isDSCR = form.financing_type === "dscr-rental";

  const next = () => {
    if (step === 1 && (!form.first_name || !form.last_name || !form.email || !form.phone)) {
      toast.error("Please complete all fields: first name, last name, email, and phone");
      return;
    }
    if (step === 3) {
      const dob = form.date_of_birth ? new Date(form.date_of_birth) : null;
      const age = dob ? (Date.now() - dob.getTime()) / (365.25 * 864e5) : 0;
      if (!dob || age < 18 || age > 110) {
        toast.error("Please enter a valid date of birth (borrower must be 18 or older)");
        return;
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo(0, 0);
  };
  const back = () => { setStep((s) => Math.max(s - 1, 0)); window.scrollTo(0, 0); };

  const submit = async () => {
    setSubmitting(true);
    const documents = [];
    Object.entries(files).forEach(([cat, arr]) => (arr || []).forEach((d) => documents.push({ ...d, category: cat })));
    const data = { ...form };
    ["first_name", "last_name", "email", "phone", "company", "property_address", "property_state"].forEach((k) => delete data[k]);
    const payload = {
      type: "loan",
      applicant_name: `${form.first_name || ""} ${form.last_name || ""}`.trim(),
      email: form.email,
      phone: form.phone || "",
      company: form.company || "",
      property_address: form.property_address || "",
      property_state: form.property_state || "",
      data,
      documents,
      referral_code: localStorage.getItem("bullseye_ref") || null,
    };
    try {
      const { data: res } = await api.post("/submissions", payload);
      localStorage.removeItem(STORAGE_KEY);
      setDone(res);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center" data-testid="apply-success">
        <div className="w-16 h-16 rounded-full bg-ok/15 grid place-items-center mx-auto"><CheckCircle2 className="w-9 h-9 text-ok" /></div>
        <h3 className="h-display text-3xl text-white mt-6">Deal Submitted</h3>
        <p className="mt-3 text-dim">Your financing application has been submitted. A BullsEye Capital representative will review it and contact you regarding next steps.</p>
        <div className="mt-6 inline-flex flex-col items-center rounded-xl border border-edge bg-surface/60 px-6 py-4">
          <span className="eyebrow">Reference</span>
          <span className="font-mono text-gold text-lg mt-1" data-testid="apply-reference">{done.reference}</span>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate("/portal")} className="btn-gold rounded-full h-12 px-6 gap-2">Track in Portal <ArrowRight className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => navigate("/")} className="btn-outline-glass rounded-full h-12 px-6">Back to Home</Button>
        </div>
        {done.lender_match?.best_match && (
          <div className="mt-10 text-left">
            <LenderMatch data={done.lender_match} variant="borrower" />
            <p className="mt-3 text-xs text-faint text-center">Sign in to your portal to request this lender and download your term sheet.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="eyebrow mb-2">Apply for Financing</div>
      <h1 className="h-display text-3xl sm:text-4xl text-white">General Financing Application</h1>

      {step > 0 && (
        <div className="mt-6" data-testid="apply-progress">
          <div className="flex justify-between text-xs text-faint mb-2">
            <span>Step {step} of {STEPS.length - 1}</span><span>{STEPS[step]}</span>
          </div>
          <Progress value={(step / (STEPS.length - 1)) * 100} className="h-1.5 bg-surface" />
        </div>
      )}

      <div className="mt-8 card-surface rounded-2xl p-6 sm:p-8">
        {step === 0 && (
          <div data-testid="apply-step-type">
            <h2 className="text-xl font-bold text-white">What type of financing do you need?</h2>
            <p className="mt-1 text-sm text-dim">Select a product to begin. EMD and Transactional Funding use a dedicated short form.</p>
            <div className="mt-6 max-w-md">
              <label className="text-sm font-medium text-white/80 mb-1.5 block">Loan Type</label>
              <Select value={form.financing_type || ""} onValueChange={chooseType}>
                <SelectTrigger className="bg-surface/70 border-edge text-white h-12" data-testid="apply-type-select"><SelectValue placeholder="Select a financing type" /></SelectTrigger>
                <SelectContent className="bg-charcoal border-edge text-white">
                  {FINANCING_TYPES.map((t) => <SelectItem key={t.value} value={t.value} className="focus:bg-gold/15 focus:text-gold">{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={() => form.financing_type && setStep(1)} disabled={!form.financing_type} className="btn-gold rounded-full h-12 px-7 gap-2 mt-6" data-testid="apply-type-continue">Continue <ArrowRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="apply-step-contact">
            <h2 className="text-xl font-bold text-white mb-1">Contact Information</h2>
            <p className="text-sm text-dim mb-5">Selected: <span className="text-gold">{typeLabel}</span></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <TextField label="First Name" name="first_name" value={form.first_name} onChange={set} required testId="apply-first-name" />
              <TextField label="Last Name" name="last_name" value={form.last_name} onChange={set} required testId="apply-last-name" />
              <TextField label="Email" name="email" type="email" value={form.email} onChange={set} required testId="apply-email" />
              <TextField label="Phone" name="phone" value={form.phone} onChange={set} required testId="apply-phone" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="apply-step-deal">
            <h2 className="text-xl font-bold text-white mb-5">Deal Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <AddressField label="Property Address" value={form.property_address} onChange={(v) => set("property_address", v)} onSelect={(a) => a.state && set("property_state", a.state)} className="sm:col-span-2" testId="apply-property-address" />
              <SelectField label="Property State" name="property_state" value={form.property_state} onChange={set} options={US_STATES} testId="apply-property-state" />
              <TextField label="Property Type" name="property_type" value={form.property_type} onChange={set} testId="apply-property-type" />
              <TextField label="Purchase Price" name="purchase_price" type="number" value={form.purchase_price} onChange={set} testId="apply-purchase-price" />
              <TextField label="Current Value / As-Is Value" name="current_value" type="number" value={form.current_value} onChange={set} testId="apply-current-value" />
              {!isDSCR && <>
                <TextField label="Estimated After Repair Value" name="arv" type="number" value={form.arv} onChange={set} testId="apply-arv" />
                <TextField label="Rehab Budget" name="rehab_budget" type="number" value={form.rehab_budget} onChange={set} testId="apply-rehab-budget" />
              </>}
              {isDSCR && <>
                <TextField label="Monthly Rent" name="monthly_rent" type="number" value={form.monthly_rent} onChange={set} testId="apply-monthly-rent" />
                <TextField label="Annual Property Taxes" name="annual_taxes" type="number" value={form.annual_taxes} onChange={set} testId="apply-annual-taxes" />
                <TextField label="Annual Insurance" name="annual_insurance" type="number" value={form.annual_insurance} onChange={set} testId="apply-annual-insurance" />
                <TextField label="Monthly HOA (if any)" name="monthly_hoa" type="number" value={form.monthly_hoa} onChange={set} testId="apply-monthly-hoa" />
              </>}
              <TextField label="Closing Date" name="closing_date" type="date" value={form.closing_date} onChange={set} testId="apply-closing-date" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="apply-step-borrower">
            <h2 className="text-xl font-bold text-white mb-5">Borrower Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <TextField label="Borrowing Entity" name="borrowing_entity" value={form.borrowing_entity} onChange={set} testId="apply-entity" />
              <TextField label="Date of Birth" name="date_of_birth" type="date" value={form.date_of_birth} onChange={set} required testId="apply-dob" />
              <SelectField label="Credit Range" name="credit_range" value={form.credit_range} onChange={set} options={CREDIT_RANGES} testId="apply-credit" />
              <TextField label="Estimated Liquidity" name="liquidity" type="number" value={form.liquidity} onChange={set} testId="apply-liquidity" />
              <TextField label="Completed Investment Deals" name="completed_deals" type="number" value={form.completed_deals} onChange={set} testId="apply-deals" />
              <TextField label="Rental Properties Owned" name="rentals_owned" type="number" value={form.rentals_owned} onChange={set} testId="apply-rentals" />
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="apply-step-docs">
            <h2 className="text-xl font-bold text-white mb-1">Documents</h2>
            <p className="text-sm text-dim mb-5">Optional now — you can also upload later from your portal.</p>
            <div className="space-y-5">
              {[
                ["Purchase Agreement", "Upload Purchase Agreement"],
                ["Scope of Work", "Upload Scope of Work"],
                ["Bank Statements", "Upload Bank Statements"],
                ["Entity Documents", "Upload Entity Documents"],
                ["Other", "Upload Other"],
              ].map(([cat, label]) => (
                <FileDropzone key={cat} label={label} category={cat} value={files[cat] || []} onChange={(arr) => setFiles((f) => ({ ...f, [cat]: arr }))} testId={`apply-upload-${cat.replace(/\s/g, "-").toLowerCase()}`} />
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <div data-testid="apply-step-review">
            <h2 className="text-xl font-bold text-white mb-5">Review & Submit</h2>
            <div className="space-y-2 text-sm">
              {[
                ["Financing Type", typeLabel],
                ["Name", `${form.first_name || ""} ${form.last_name || ""}`],
                ["Email", form.email], ["Phone", form.phone], ["Date of Birth", form.date_of_birth],
                ["Property", `${form.property_address || ""} ${form.property_state || ""}`],
                ["Documents", Object.values(files).flat().length + " uploaded"],
              ].filter(([, v]) => v && String(v).trim()).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-edge/60 py-2">
                  <span className="text-dim">{k}</span><span className="text-white text-right">{v}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-faint">Submitting does not constitute an approval, commitment, or offer of financing. All financing is subject to underwriting, approval, documentation, availability, and applicable law.</p>
          </div>
        )}

        {step > 0 && (
          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" onClick={back} className="text-white/70 gap-1" data-testid="apply-back"><ArrowLeft className="w-4 h-4" /> Back</Button>
            {step < 5 ? (
              <Button onClick={next} className="btn-gold rounded-full h-12 px-7 gap-2" data-testid="apply-next">Continue <ArrowRight className="w-4 h-4" /></Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="btn-gold rounded-full h-12 px-7 gap-2" data-testid="apply-submit">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><Check className="w-4 h-4" /> Submit Deal</>}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
